import { useState, useMemo } from 'react'
import { getPosProducts, savePosSale, voidPosSale, getPosSales } from '../../lib/posStore'
import { getPosDepartments } from '../../data/posSeed'
import { useAuth } from '../../context/AuthContext'
import { useDepartment } from '../../context/DepartmentContext'
import {
  ShoppingCart, Plus, Minus, X, Trash2, Check, ReceiptText, RotateCcw
} from 'lucide-react'

/* ─── helpers ─── */
const R = (v) => `R${Number(v || 0).toFixed(2)}`
const todayStr = () => new Date().toISOString().split('T')[0]

export default function PosTerminal() {
  const { userProfile } = useAuth()
  const staffId = userProfile?.staffId || userProfile?.uid || 'unknown'
  const { currentDepartmentId } = useDepartment()

  /* ─── department tab config ─── */
  const TABS = useMemo(
    () => getPosDepartments(currentDepartmentId).map((d) => ({ id: d.id, label: d.label })),
    [currentDepartmentId]
  )

  /* ── state ── */
  const [activeDept, setActiveDept] = useState(() => TABS[0]?.id || '')
  const [cart, setCart] = useState([])
  const [toast, setToast] = useState(null)
  const [showCashUp, setShowCashUp] = useState(false)
  const [cashUpKey, setCashUpKey] = useState(0) // bump to refresh cash-up data
  const [mobileCartOpen, setMobileCartOpen] = useState(false)

  /* ── products for active tab ── */
  const products = useMemo(() => {
    const all = getPosProducts(activeDept, currentDepartmentId)
    return all.filter((p) => p.isActive !== false)
  }, [activeDept, currentDepartmentId])

  /* ── cart helpers ── */
  const addToCart = (product) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.productId === product.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 }
        return next
      }
      return [
        ...prev,
        {
          productId: product.id,
          code: product.code,
          name: product.name,
          department: product.department,
          unitPrice: product.sellingPrice,
          qty: 1,
        },
      ]
    })
  }

  const updateQty = (productId, delta) => {
    setCart((prev) => {
      const next = prev
        .map((c) =>
          c.productId === productId ? { ...c, qty: c.qty + delta } : c
        )
        .filter((c) => c.qty > 0)
      return next
    })
  }

  const removeItem = (productId) => {
    setCart((prev) => prev.filter((c) => c.productId !== productId))
  }

  const clearCart = () => setCart([])

  const cartTotal = useMemo(
    () => cart.reduce((s, c) => s + c.unitPrice * c.qty, 0),
    [cart]
  )

  const cartCount = useMemo(
    () => cart.reduce((s, c) => s + c.qty, 0),
    [cart]
  )

  /* ── complete sale ── */
  const completeSale = () => {
    if (cart.length === 0) return
    const sale = {
      date: todayStr(),
      staffId,
      items: cart.map((c) => ({
        productId: c.productId,
        code: c.code,
        name: c.name,
        department: c.department,
        unitPrice: c.unitPrice,
        qty: c.qty,
        lineTotal: c.unitPrice * c.qty,
      })),
      total: cartTotal,
      paymentMethod: 'cash',
      departmentId: currentDepartmentId,
    }
    savePosSale(sale)
    clearCart()
    setCashUpKey((k) => k + 1)
    showToast('Sale completed!')
  }

  /* ── void a sale (cash-up) ── */
  const handleVoid = (saleId) => {
    const reason = 'Voided from POS terminal'
    voidPosSale(saleId, reason)
    setCashUpKey((k) => k + 1)
    showToast('Sale voided')
  }

  /* ── toast ── */
  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  /* ── daily cash-up data ── */
  const cashUpData = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const _k = cashUpKey // dependency trigger
    const today = todayStr()
    const sales = getPosSales(currentDepartmentId, today, today)
    const active = sales.filter((s) => !s.voided)
    const voided = sales.filter((s) => s.voided)

    // per-department totals
    const deptTotals = {}
    for (const dept of TABS) {
      deptTotals[dept.id] = { label: dept.label, revenue: 0, count: 0 }
    }
    for (const sale of active) {
      for (const item of sale.items || []) {
        const dId = item.department || 'misc'
        if (!deptTotals[dId]) {
          deptTotals[dId] = { label: dId, revenue: 0, count: 0 }
        }
        deptTotals[dId].revenue += item.lineTotal || item.unitPrice * item.qty
        deptTotals[dId].count += item.qty
      }
    }

    const totalRevenue = active.reduce((s, sl) => s + (sl.total || 0), 0)
    return { sales, active, voided, deptTotals, totalRevenue, totalTx: active.length }
  }, [cashUpKey, currentDepartmentId])

  /* ────────────────────────────────────
     RENDER
  ──────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* ── Toast ── */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Check size={18} />
          <span className="font-medium text-sm">{toast}</span>
        </div>
      )}

      {/* ── Header ── */}
      <header className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <ShoppingCart size={22} className="text-brand-500" />
          <h1 className="text-lg font-bold tracking-wide">POS Terminal</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* mobile cart toggle */}
          <button
            onClick={() => setMobileCartOpen((o) => !o)}
            className="lg:hidden relative bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          >
            <ShoppingCart size={16} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-brand-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowCashUp((v) => !v)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-700 transition-colors"
          >
            <ReceiptText size={16} />
            <span className="hidden sm:inline">Daily Cash-Up</span>
          </button>
        </div>
      </header>

      {/* ── Department Tabs ── */}
      <nav className="bg-gray-900 border-b border-gray-800 px-4 py-2 overflow-x-auto shrink-0">
        <div className="flex gap-2 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveDept(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                activeDept === tab.id
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Main content: product grid + cart ── */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* ── Product Grid ── */}
        <main className="flex-1 overflow-y-auto p-4">
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <ShoppingCart size={40} className="mb-3 opacity-40" />
              <p className="text-sm">No products in this department</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.map((p) => {
                const inCart = cart.find((c) => c.productId === p.id)
                return (
                  <button
                    key={p.id}
                    onClick={() => addToCart(p)}
                    className={`relative bg-gray-900 border rounded-lg p-4 text-left transition-all hover:border-brand-600 hover:bg-gray-800 active:scale-[0.97] ${
                      inCart
                        ? 'border-brand-600 ring-1 ring-brand-600/30'
                        : 'border-gray-800'
                    }`}
                    style={{ minHeight: '80px' }}
                  >
                    {inCart && (
                      <span className="absolute top-2 right-2 bg-brand-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                        {inCart.qty}
                      </span>
                    )}
                    <p className="font-semibold text-sm text-white leading-tight mb-1">
                      {p.name}
                    </p>
                    <p className="text-[11px] text-gray-500 mb-2">{p.code}</p>
                    <p className="text-brand-400 font-bold text-base">
                      {R(p.sellingPrice)}
                    </p>
                  </button>
                )
              })}
            </div>
          )}
        </main>

        {/* ── Cart Sidebar (desktop) / Bottom Sheet (mobile) ── */}
        <aside
          className={`bg-gray-900 border-gray-800 flex flex-col shrink-0 transition-all duration-300 ${
            /* desktop: always visible right sidebar */
            'lg:w-96 lg:border-l lg:relative lg:translate-y-0'
          } ${
            /* mobile: bottom sheet */
            mobileCartOpen
              ? 'fixed inset-x-0 bottom-0 z-40 h-[70vh] border-t rounded-t-2xl lg:static lg:h-auto lg:rounded-none'
              : 'fixed inset-x-0 -bottom-full z-40 lg:static lg:translate-y-0'
          }`}
        >
          {/* mobile drag handle */}
          <div className="lg:hidden flex justify-center py-2">
            <div className="w-10 h-1 bg-gray-700 rounded-full" />
          </div>

          {/* cart header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <ShoppingCart size={18} className="text-brand-500" />
              <h2 className="font-bold text-sm">
                Cart{cartCount > 0 && ` (${cartCount})`}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="text-gray-500 hover:text-red-400 transition-colors p-1"
                  title="Clear cart"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                onClick={() => setMobileCartOpen(false)}
                className="lg:hidden text-gray-500 hover:text-white p-1"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* cart items */}
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-600">
                <ShoppingCart size={32} className="mb-2 opacity-30" />
                <p className="text-xs">Cart is empty</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {cart.map((item) => (
                  <li
                    key={item.productId}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex items-center gap-3"
                  >
                    {/* info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {R(item.unitPrice)} each
                      </p>
                    </div>

                    {/* qty controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateQty(item.productId, -1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center text-sm font-bold">
                        {item.qty}
                      </span>
                      <button
                        onClick={() => updateQty(item.productId, 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    {/* line total + remove */}
                    <div className="text-right flex items-center gap-2">
                      <span className="text-sm font-bold text-brand-400 w-16 text-right">
                        {R(item.unitPrice * item.qty)}
                      </span>
                      <button
                        onClick={() => removeItem(item.productId)}
                        className="text-gray-600 hover:text-red-400 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* cart footer: totals + actions */}
          <div className="border-t border-gray-800 px-4 py-4 space-y-3">
            {/* subtotal & total */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Subtotal</span>
                <span>{R(cartTotal)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-brand-400">{R(cartTotal)}</span>
              </div>
            </div>

            {/* buttons */}
            <div className="flex gap-2">
              <button
                onClick={clearCart}
                disabled={cart.length === 0}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw size={16} />
                Clear
              </button>
              <button
                onClick={completeSale}
                disabled={cart.length === 0}
                className="flex-[2] flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 rounded-lg px-4 py-3 text-sm font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Check size={16} />
                Complete Sale
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* ── Mobile cart FAB (when sheet closed & cart non-empty) ── */}
      {!mobileCartOpen && cartCount > 0 && (
        <button
          onClick={() => setMobileCartOpen(true)}
          className="lg:hidden fixed bottom-4 right-4 z-30 bg-brand-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg shadow-brand-600/30"
        >
          <ShoppingCart size={22} />
          <span className="absolute -top-1 -right-1 bg-white text-brand-700 text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
            {cartCount}
          </span>
        </button>
      )}

      {/* ── Mobile cart backdrop ── */}
      {mobileCartOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setMobileCartOpen(false)}
        />
      )}

      {/* ── Daily Cash-Up Panel ── */}
      {showCashUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* cash-up header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <ReceiptText size={20} className="text-brand-500" />
                <div>
                  <h2 className="font-bold text-base">Daily Cash-Up</h2>
                  <p className="text-xs text-gray-500">{todayStr()}</p>
                </div>
              </div>
              <button
                onClick={() => setShowCashUp(false)}
                className="text-gray-500 hover:text-white p-1"
              >
                <X size={20} />
              </button>
            </div>

            {/* cash-up body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">Total Revenue</p>
                  <p className="text-xl font-bold text-brand-400">
                    {R(cashUpData.totalRevenue)}
                  </p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">Transactions</p>
                  <p className="text-xl font-bold text-white">
                    {cashUpData.totalTx}
                  </p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">Voided</p>
                  <p className="text-xl font-bold text-red-400">
                    {cashUpData.voided.length}
                  </p>
                </div>
              </div>

              {/* per-department breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-3">
                  Revenue by Department
                </h3>
                <div className="space-y-2">
                  {Object.entries(cashUpData.deptTotals)
                    .filter(([, v]) => v.revenue > 0 || v.count > 0)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .map(([id, data]) => (
                      <div
                        key={id}
                        className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-white">
                            {data.label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {data.count} items sold
                          </p>
                        </div>
                        <p className="text-sm font-bold text-brand-400">
                          {R(data.revenue)}
                        </p>
                      </div>
                    ))}
                  {Object.values(cashUpData.deptTotals).every(
                    (v) => v.revenue === 0
                  ) && (
                    <p className="text-gray-600 text-sm text-center py-4">
                      No sales recorded today
                    </p>
                  )}
                </div>
              </div>

              {/* individual sales list */}
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-3">
                  Transactions
                </h3>
                <div className="space-y-2">
                  {cashUpData.sales.length === 0 ? (
                    <p className="text-gray-600 text-sm text-center py-4">
                      No transactions today
                    </p>
                  ) : (
                    cashUpData.sales.map((sale) => (
                      <div
                        key={sale.id}
                        className={`bg-gray-800 border rounded-lg px-4 py-3 ${
                          sale.voided
                            ? 'border-red-900/50 opacity-60'
                            : 'border-gray-700'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-white">
                                {R(sale.total)}
                              </p>
                              {sale.voided && (
                                <span className="bg-red-900/40 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded">
                                  VOIDED
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {(sale.items || [])
                                .map(
                                  (i) =>
                                    `${i.qty}x ${i.name}`
                                )
                                .join(', ')}
                            </p>
                            <p className="text-[10px] text-gray-600 mt-1">
                              {sale.createdAt
                                ? new Date(sale.createdAt).toLocaleTimeString()
                                : ''}
                            </p>
                          </div>
                          {!sale.voided && (
                            <button
                              onClick={() => handleVoid(sale.id)}
                              className="ml-3 text-gray-600 hover:text-red-400 transition-colors p-1"
                              title="Void sale"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* cash-up footer */}
            <div className="border-t border-gray-800 px-6 py-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">End-of-day total</p>
                <p className="text-xl font-bold text-brand-400">
                  {R(cashUpData.totalRevenue)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
